/// <amd-module name="Wit/WorkItemForm/Links/RemoteWorkItem/RemoteWorkItemService" />
import { getContributionsClient } from "VSS/Platform/RestClient/Contributions";
import { IAuthenticationService } from "VSS/Platform/Authentication";
import { IAuthorizationTokenProvider, IVssPageContext, IVssService, Services, TeamFoundationHostType, VssService  } from "VSS/Platform/Context";
import { IStateColor } from "Wit/WorkItemDataManager/Contracts";
import { IVssContributionService } from "VSS/Platform/Contribution";

export const IRemoteWorkItemServiceId = "IRemoteWorkItemService";

export interface IRemoteWorkItemService extends IVssService {
    /**
     * Given an array of IDs and the host and project to which they belong, 
     * returns data about the work item, its fields, and the colors and icons 
     * associated with its type and state.
     */
    getRemoteWorkItemsAndTypes(ids: number[], remoteHostUrl: string, remoteProjectId: string): Promise<IRemoteWorkItemAndTypeData[]>;

    /**
     * Given the url of a remote work item, returns data about the work item,
     * its fields, and the remote host and project to which it belongs. Returns
     * undefined if the url does not point to an accessible work item.
     * @param remoteWorkItemUrl 
     */
    getRemoteWorkItemAndHost(remoteWorkItemUrl: string): Promise<IRemoteWorkItemAndHostData | undefined>;
}

const RemoteWorkitemDataProvider = "ms.vss-work-web.remote-workitem-data-provider";
const RemoteWorkitemByUrlDataProvider = "ms.vss-work-web.remote-workitem-url-data-provider";
const RemoteWorkitemTokenProvider = "ms.vss-work-web.remote-workitem-token-data-provider";

interface IRemoteWorkItemsDataProviderPayload {
    "work-items-data": IRemoteWorkItemAndTypeData[];
}

export interface IRemoteWorkItemAndTypeData {
    "work-item-data": IRemoteWorkItemData;
    "work-item-type-state-color": IStateColor;
    "work-item-type-color-icon": IRemoteWorkItemTypeData;
}

interface IRemoteWorkItemData {
    id: number;
    fields: { [refName: string]: unknown };
}

interface IRemoteWorkItemSessionToken {
    appId: string;
    token: string;
    validTo: Date;
}

interface IRemoteWorkItemSessionTokenModel {
    remoteWorkItemSessionToken: IRemoteWorkItemSessionToken
}

interface IRemoteWorkItemTypeData {
    workItemTypeName: string;
    color: string;
    icon: string;
}

export interface IRemoteWorkItemAndHostData {
    "work-item-host-id": string;
    "work-item-host-name": string;
    "work-item-host-url": string;
    "work-item-project-id": string;
    "work-item-data": IRemoteWorkItemData;
}
class RemoteWorkItemService extends VssService implements IRemoteWorkItemService {
    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
    }

    public async getRemoteWorkItemsAndTypes(ids: number[], remoteHostUrl: string, remoteProjectId: string): Promise<IRemoteWorkItemAndTypeData[]> {
        const rootPath = remoteHostUrl.endsWith("/") ? remoteHostUrl : remoteHostUrl + "/";

        const authTokenProvider = await this.getRemoteTokenProvider();        

        const query = {
            contributionIds: [RemoteWorkitemDataProvider],
            context: { properties: { ids } }
        };

        const contributionClient = getContributionsClient(this.pageContext, {rootPath, hostType: TeamFoundationHostType.ProjectCollection, authTokenProvider});
        try {
            const result = await contributionClient.queryDataProviders(query, "project", remoteProjectId);
            const payload = result.data[RemoteWorkitemDataProvider] as IRemoteWorkItemsDataProviderPayload;
            return payload["work-items-data"] ?? [];
        } catch (e: unknown) {
            console.error(`Unable to load work items ${ids.join(", ")} from host ${remoteHostUrl} and project ${remoteProjectId}`);
            return [];
        }
    }

    public async getRemoteWorkItemAndHost(remoteWorkItemUrl: string): Promise<IRemoteWorkItemAndHostData | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const remoteWorkItemData = await contributionService.getDataAsync<IRemoteWorkItemAndHostData | null>(RemoteWorkitemByUrlDataProvider, {url: remoteWorkItemUrl});
        return remoteWorkItemData ?? undefined;
    }

    private async getRemoteTokenProvider(): Promise<IAuthorizationTokenProvider> {
        const authService = this.pageContext.getService<IAuthenticationService>("IAuthenticationService");
        const remoteWorkitemTokenModel = await this.getRemoteWorkitemToken();
        return authService.getBearerTokenProvider(remoteWorkitemTokenModel.token);
    }

    private async getRemoteWorkitemToken(): Promise<IRemoteWorkItemSessionToken> {
        const contributionClient = getContributionsClient(this.pageContext);
        const queryToken = {
            contributionIds: [RemoteWorkitemTokenProvider],
            context: { properties: { ids: [] } }
        };
        
        try {
            const result = await contributionClient.queryDataProviders(queryToken);
            const payload = result.data[RemoteWorkitemTokenProvider] as IRemoteWorkItemSessionTokenModel;
            return payload.remoteWorkItemSessionToken;
        } catch (e: unknown) {
            console.error(`Authentication error: ${e}`);
            throw e;
        }
    }
}

Services.add(IRemoteWorkItemServiceId, { serviceFactory: RemoteWorkItemService });
